import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { useAtomValue } from 'jotai';
import {
  ACTIVE_COLOR,
  colorForArea,
} from 'components/type-curve-workspace/area-colors';
import type { InventoryWorkspaceMapArea } from 'types/api-responses';
import {
  typeCurveAreaVisibilityAtom,
  tcInventoryHighlightedAreaAtom,
} from './atoms';

// Every area is drawn at the same weight so no colour reads as more present
// than another; only the highlighted area's fill lifts out of the set. Mirrors
// the create-inventory-workspace modal's preview (inventory-workspace-area-preview.tsx).
const FILL_OPACITY_ACTIVE = 0.55;
const FILL_OPACITY_AREA = 0.14;
// The selected area's border lifts out; the rest recede to thin, faded outlines.
const STROKE_WIDTH_ACTIVE = 3;
const STROKE_WIDTH_AREA = 1.5;
const STROKE_OPACITY_ACTIVE = 1;
const STROKE_OPACITY_AREA = 0.5;

interface TcInventoryAreasLayerProps {
  /** Workspace areas with inline GeoJSON, target first — the index fixes each
   *  area's colour slot. Straight from the show payload's `map.areas`. */
  areas: InventoryWorkspaceMapArea[];
}

/**
 * Draws the Type Curve areas this workspace covers on the shared `<Map>`.
 *
 * Must be rendered as a child of `<Map>` (its `<Source>`/`<Layer>` read the
 * react-map-gl map context). Geometry arrives inline on the show payload, so
 * there is no fetch here. The selected type curve's area (driven from the tree
 * selection via `tcInventoryHighlightedAreaAtom`) gets the gold active fill and
 * a bold border; all others use their palette colour at a faded, thin outline.
 *
 * The payload also carries the launched area's co-developed neighbours as
 * context; those are NOT drawn (see `drawn` below).
 * Display-only: no click handler, so it never competes with the map's AOI /
 * dev-area interactions.
 *
 * @param {TcInventoryAreasLayerProps} props - The ordered areas with geometry.
 * @return {JSX.Element | null} One source (fill + outline) per covered area.
 */
export const TcInventoryAreasLayer = ({
  areas,
}: TcInventoryAreasLayerProps) => {
  const highlightedName = useAtomValue(tcInventoryHighlightedAreaAtom);
  // Driven by the map's "Type Curve Area" toggle. Hidden via layout rather than
  // by unmounting, so toggling does not tear down and rebuild a source per area.
  const areasVisible = useAtomValue(typeCurveAreaVisibilityAtom);
  const areaLayout = useMemo(
    () => ({
      visibility: areasVisible ? ('visible' as const) : ('none' as const),
    }),
    [areasVisible]
  );

  // Keep the index-based colour slot stable (origin = slot 0), then drop the
  // areas we do not draw. Indexing FIRST is what keeps the colours stable: the
  // slot is the area's position in the payload, so filtering before assigning it
  // would re-colour the remaining areas as the neighbour set changed.
  //
  // Dropped here: a co-developed NEIGHBOUR (`isCovered: false`), which the
  // payload sends as map context only — this workspace holds no zones in it, and
  // drawing it read as if the area were one of the workspace's own. Also any
  // area that arrived without geometry, which there is nothing to draw for.
  //
  // Tested against `false` rather than falsiness so a payload that somehow omits
  // the flag still draws: a stray area on the map is noticed and understood, an
  // empty map is neither.
  const drawn = useMemo(
    () =>
      areas
        .map((area, index) => {
          if (area.isCovered === false) return null;
          return area.geometry
            ? { name: area.name, index, geometry: area.geometry }
            : null;
        })
        .filter(
          (
            a
          ): a is { name: string; index: number; geometry: GeoJSON.Geometry } =>
            Boolean(a)
        ),
    [areas]
  );

  return (
    <>
      {drawn.map((area) => {
        const isActive = area.name === highlightedName;
        const color = colorForArea(area.index);

        return (
          <Source
            key={area.name}
            id={`tc-inventory-area-${area.index}`}
            type="geojson"
            data={area.geometry}
          >
            <Layer
              id={`tc-inventory-area-fill-${area.index}`}
              type="fill"
              layout={areaLayout}
              paint={{
                'fill-color': isActive ? ACTIVE_COLOR : color,
                'fill-opacity': isActive
                  ? FILL_OPACITY_ACTIVE
                  : FILL_OPACITY_AREA,
              }}
            />
            <Layer
              id={`tc-inventory-area-line-${area.index}`}
              type="line"
              layout={areaLayout}
              paint={{
                'line-color': isActive ? ACTIVE_COLOR : color,
                'line-width': isActive
                  ? STROKE_WIDTH_ACTIVE
                  : STROKE_WIDTH_AREA,
                'line-opacity': isActive
                  ? STROKE_OPACITY_ACTIVE
                  : STROKE_OPACITY_AREA,
              }}
            />
          </Source>
        );
      })}
    </>
  );
};
